import { useEffect, useRef, useState } from "react";
import { Layers, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { featureInfo } from "@/lib/featureInfo";

const boundsOf = (geom) => {
  const pts = [];
  const walk = (c) => (typeof c[0] === "number" ? pts.push(c) : c.forEach(walk));
  if (geom?.coordinates) walk(geom.coordinates);
  if (!pts.length) return null;
  const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1]);
  return [[Math.min(...ys), Math.min(...xs)], [Math.max(...ys), Math.max(...xs)]];
};

// Searches server-side library layers + browser-only temporary layers by any attribute value.
export function LayerSearchResults({ q, layers, onPick }) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(true);
  const ref = useRef(null);

  useEffect(() => {
    const needle = q.trim().toLowerCase();
    if (needle.length < 1) { setResults([]); return; }
    let cancelled = false;
    const local = [];
    layers.filter((l) => l.temp && l.geojson).forEach((l) => l.geojson.features.forEach((f) => {
      const hit = Object.entries(f.properties || {}).find(([, v]) => v != null && typeof v !== "object" && String(v).toLowerCase().includes(needle));
      if (hit) local.push({ layer_id: l.id, layer_name: l.name, field: hit[0], value: String(hit[1]), properties: f.properties, geometry: f.geometry, bounds: boundsOf(f.geometry) });
    }));
    const t = setTimeout(() => {
      api.get("/map/layers/search", { params: { q: needle } }).then(({ data }) => { if (!cancelled) { setResults([...local, ...data].slice(0, 20)); setOpen(true); } }).catch(() => { if (!cancelled) setResults(local); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, layers]);

  useEffect(() => {
    const close = (e) => { if (!ref.current?.contains(e.target) && !e.target.closest?.("[data-testid='parcel-search-input']")) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  useEffect(() => { if (q) setOpen(true); }, [q]);

  if (!open || !q.trim() || results.length === 0) return null;
  return (
    <div ref={ref} data-testid="layer-search-results" onMouseDown={(e) => e.stopPropagation()} className="absolute top-12 left-0 w-80 glass rounded-2xl shadow-2xl p-2 max-h-80 overflow-auto fade-up z-[1300]">
      <p className="px-3 pt-1 pb-1 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">In uploaded layers · {results.length}</p>
      {results.map((r, i) => {
        const layer = layers.find((l) => l.id === r.layer_id) || { id: r.layer_id, name: r.layer_name };
        return (
          <button key={`${r.layer_id}-${i}`} type="button" data-testid={`layer-search-result-${i}`}
            onClick={() => { setOpen(false); onPick(layer, r, featureInfo(layer, { properties: r.properties, geometry: r.geometry }, r.bounds ? { lat: (r.bounds[0][0] + r.bounds[1][0]) / 2, lng: (r.bounds[0][1] + r.bounds[1][1]) / 2 } : null)); }}
            className="w-full text-left px-3 py-2 rounded-xl hover:bg-muted flex items-start gap-2">
            <MapPin size={14} className="mt-0.5 shrink-0 text-emerald-700 dark:text-emerald-300" />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium truncate">{r.value}</span>
              <span className="block text-[11px] text-muted-foreground truncate"><span className="font-mono">{r.field}</span> · <Layers size={10} className="inline -mt-0.5" /> {r.layer_name}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
